/**
 * Scheduled emails: Mana Pricing's own schedules, the schedules still sending from the Pricing
 * and Other Lenders modules (preview the new email, send a test to yourself, then move each one),
 * and the rates sent log with the lender behind every emailed rate.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MANA_PRICING_SOURCES, MANA_SOURCE_LABELS, UWM_PROGRAMS, type ManaGridCell, type ManaOptionsSentRow, type ManaPricingSource, type ManaSendLogDto } from '@mana/shared';
import { api } from '../../lib/api.js';
import { Badge } from '../../ui/Card.js';
import { Button } from '../../ui/Button.js';
import { EmailPreviewDialog, GridCellsTable, OptionsRowsTable, RealtorCellsTable, isRealtorCells, type PreviewContent } from './EmailPreview.js';
import {
  SCHEDULES_KEY,
  errText,
  fmtInterval,
  fmtWhen,
  isoToLocalInput,
  localInputToIso,
  type LegacyScheduleDto,
  type Msg,
  type PreviewDto,
  type ScheduleDto,
  type SchedulesResponse,
} from './shared.js';

export interface SentLogTarget {
  title: string;
  scheduleId?: string;
  borrowerName?: string;
}

const programsLabel = (keys: string[]) => keys.map((k) => UWM_PROGRAMS.find((p) => p.key === k)?.label ?? k).join(', ');
const lendersLabel = (lenders: ManaPricingSource[]) =>
  MANA_PRICING_SOURCES.every((l) => lenders.includes(l)) ? 'All lenders' : lenders.map((l) => MANA_SOURCE_LABELS[l]).join(', ');

function BorrowerCell({ name, email, onOpen }: { name: string; email: string; onOpen?: () => void }) {
  return (
    <div className="min-w-[11rem] flex-[1_1_12rem]">
      {onOpen ? (
        <button type="button" onClick={onOpen} title="Show the scenario this email sends in the panel — edit and Save to change it" className="block max-w-full truncate text-left text-sm font-medium text-brand-navy-800 hover:text-brand-navy-600 hover:underline">
          {name}
        </button>
      ) : (
        <p className="truncate text-sm font-medium text-brand-navy-800" title={name}>
          {name}
        </p>
      )}
      <p className="truncate text-xs text-slate-500" title={email}>
        {email}
      </p>
    </div>
  );
}

function WhatCell({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="min-w-[12rem] flex-[1.4_1_14rem] text-xs">
      <p className="font-medium text-slate-700">{title}</p>
      <p className="truncate text-slate-500" title={detail}>
        {detail}
      </p>
    </div>
  );
}

function StatusCell({ active, lastStatus, nextRunAt, intervalMinutes, sentCount }: { active: boolean; lastStatus: string | null; nextRunAt: string; intervalMinutes: number; sentCount: number }) {
  const failing = active && !!lastStatus && lastStatus !== 'ok';
  return (
    <div className="w-44 shrink-0 text-xs">
      <p>
        {!active ? (
          <Badge tone="neutral">Paused</Badge>
        ) : failing ? (
          <span className="font-medium text-rose-600" title={lastStatus ?? ''}>
            Last send failed
          </span>
        ) : (
          <span className="text-slate-700">Next {fmtWhen(nextRunAt)}</span>
        )}
      </p>
      <p className="mt-0.5 text-slate-400">
        Every {fmtInterval(intervalMinutes)} · {sentCount} sent
      </p>
    </div>
  );
}

/** "⋯" menu for a row's less frequent actions. */
function RowMenu({ label, items }: { label: string; items: Array<{ label: string; onClick: () => void; danger?: boolean }> }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <Button size="sm" variant="ghost" aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="px-2">
        <span aria-hidden className="text-base leading-none">
          ⋯
        </span>
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-30 mt-1 w-44 rounded-lg border border-slate-200 bg-white py-1 text-xs shadow-floating">
          {items.map((it) => (
            <button
              key={it.label}
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
              className={'block w-full px-3 py-1.5 text-left hover:bg-slate-50 ' + (it.danger ? 'text-rose-600' : 'text-slate-700')}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ManaSchedules({ data, onShowSent, onOpenScenario }: { data: SchedulesResponse | undefined; onShowSent: (t: SentLogTarget) => void; onOpenScenario: (s: ScheduleDto) => void }) {
  const qc = useQueryClient();
  const schedules = data?.schedules ?? [];
  const legacy = data?.legacy ?? [];
  const [msg, setMsg] = useState<Msg>(null);
  const [preview, setPreview] = useState<{ content: PreviewContent; legacy?: LegacyScheduleDto } | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editStart, setEditStart] = useState('');
  const invalidate = () => void qc.invalidateQueries({ queryKey: SCHEDULES_KEY });

  const patchMut = useMutation({
    mutationFn: (v: { id: string; active?: boolean; startAt?: string }) => api<{ schedule: ScheduleDto }>(`/api/mana-pricing/schedules/${v.id}`, { method: 'PATCH', body: JSON.stringify({ active: v.active, startAt: v.startAt }) }),
    onSuccess: () => {
      setEditId(null);
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Update failed') }),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/mana-pricing/schedules/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Cancel failed') }),
  });
  const previewMut = useMutation({
    mutationFn: (s: ScheduleDto) => api<PreviewDto>(`/api/mana-pricing/schedules/${s.id}/preview`, { method: 'POST', body: '{}' }),
    onSuccess: (r, s) => setPreview({ content: { title: `Next email for ${s.borrowerName}`, subject: r.subject, html: r.html, cells: r.cells, rows: r.rows, notes: r.notes } }),
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Preview failed') }),
  });
  const testMut = useMutation({
    mutationFn: (s: ScheduleDto) => api<{ ok: boolean; sentTo: string }>(`/api/mana-pricing/schedules/${s.id}/test`, { method: 'POST', body: '{}' }),
    onSuccess: (r) => setMsg({ tone: 'ok', text: `Test sent to ${r.sentTo} — the borrower got nothing.` }),
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Test failed') }),
  });
  const legacyPreviewMut = useMutation({
    mutationFn: (s: LegacyScheduleDto) =>
      api<{ format: 'grid' | 'options'; mana: Omit<PreviewDto, 'notes'> | null; error: { error: string; message: string } | null; current: { subject: string; html: string } | null; notes: string[] }>(
        `/api/mana-pricing/legacy-schedules/${s.source}/${s.id}/preview`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r, s) =>
      setPreview({
        legacy: s,
        content: {
          title: `${s.borrowerName} — the email after moving to Mana Pricing`,
          subject: r.mana?.subject ?? null,
          html: r.mana?.html ?? null,
          currentHtml: r.current?.html ?? null,
          cells: r.mana?.cells ?? [],
          rows: r.mana?.rows ?? [],
          notes: r.notes,
          error: r.error?.message ?? null,
        },
      }),
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Preview failed') }),
  });
  const legacyTestMut = useMutation({
    mutationFn: (s: LegacyScheduleDto) => api<{ ok: boolean; sentTo: string }>(`/api/mana-pricing/legacy-schedules/${s.source}/${s.id}/test`, { method: 'POST', body: '{}' }),
    onSuccess: (r) => setMsg({ tone: 'ok', text: `Test sent to ${r.sentTo} — the borrower got nothing.` }),
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Test failed') }),
  });
  const moveMut = useMutation({
    mutationFn: (s: LegacyScheduleDto) => api<{ ok: boolean; schedule: ScheduleDto | null }>(`/api/mana-pricing/legacy-schedules/${s.source}/${s.id}/move`, { method: 'POST', body: '{}' }),
    onSuccess: (r, s) => {
      setPreview(null);
      setMsg({ tone: 'ok', text: `Moved ${s.borrowerName}'s schedule — next send ${fmtWhen(r.schedule?.nextRunAt ?? s.nextRunAt)} from Mana Pricing.` });
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Move failed') }),
  });
  const confirmMove = (s: LegacyScheduleDto) => {
    const what =
      s.format === 'grid'
        ? 'Each rate in the email becomes the best rate across every lender, UWM live and the rate sheets (same format, programs, CYP and send time). The programs become the scenario\'s Temporary Buydown selection, so they follow the scenario from now on.'
        : 'The same options are re-priced on every send, as today.';
    const who = `The retired ${s.source === 'pricing' ? 'Pricing' : 'Other Lenders Pricing'} module no longer sends it; Mana Pricing takes over.`;
    if (window.confirm(`Move ${s.borrowerName}'s schedule to Mana Pricing?\n\n${what}\n\n${who} Next send: ${fmtWhen(s.nextRunAt)}.`)) {
      moveMut.mutate(s);
    }
  };

  if (schedules.length === 0 && legacy.length === 0) return null;
  const active = schedules.filter((s) => s.active).length;
  const failingCount = schedules.filter((s) => s.active && s.lastStatus && s.lastStatus !== 'ok').length;
  return (
    <div className="space-y-4">
      {msg && <p className={'rounded-lg px-3 py-2 text-xs ' + (msg.tone === 'err' ? 'border border-rose-200 bg-rose-50 text-rose-700' : 'border border-emerald-200 bg-emerald-50 text-emerald-800')}>{msg.text}</p>}

      {legacy.length > 0 && (
        <div className="surface-card">
          <div className="border-b border-slate-100 px-5 py-3">
            <h3 className="text-sm font-semibold text-brand-navy-800">Schedules from the old modules</h3>
            <p className="mt-0.5 max-w-2xl text-xs text-slate-500">
              {/* Both old modules were retired on 2026-10-11: their schedulers are gone, so a schedule still filed under one does not send until moved. */}
              {legacy.length === 1 ? '1 schedule is' : `${legacy.length} schedules are`} still filed under the retired Pricing / Other Lenders Pricing modules, which no longer send — move {legacy.length === 1 ? 'it' : 'them'} so the borrower keeps getting rates. Preview the new email, send a test to yourself, then move.
            </p>
          </div>
          <ul className="divide-y divide-slate-100">
            {legacy.map((s) => (
              <li key={`${s.source}:${s.id}`} className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3">
                <BorrowerCell name={s.borrowerName} email={s.toEmail} />
                <WhatCell
                  title={s.format === 'grid' ? 'Best-rates email' : `Options email · ${s.productIds.length || 'all'} option${s.productIds.length === 1 ? '' : 's'}`}
                  detail={[s.format === 'grid' ? programsLabel(s.programKeys) : null, s.cypBps ? `CYP ${s.cypBps}` : null, `from ${s.source === 'pricing' ? 'Pricing' : 'Other Lenders'}`].filter(Boolean).join(' · ')}
                />
                <StatusCell active={s.active} lastStatus={s.lastStatus} nextRunAt={s.nextRunAt} intervalMinutes={s.intervalMinutes} sentCount={s.sentCount} />
                <div className="ml-auto flex shrink-0 items-center gap-1">
                  <Button size="sm" variant="ghost" disabled={legacyPreviewMut.isPending} onClick={() => legacyPreviewMut.mutate(s)}>
                    {legacyPreviewMut.isPending && legacyPreviewMut.variables?.id === s.id ? 'Pricing…' : 'Preview'}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={legacyTestMut.isPending} onClick={() => legacyTestMut.mutate(s)}>
                    {legacyTestMut.isPending && legacyTestMut.variables?.id === s.id ? 'Sending…' : 'Test to me'}
                  </Button>
                  <Button size="sm" variant="secondary" disabled={moveMut.isPending} onClick={() => confirmMove(s)}>
                    Move
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {schedules.length > 0 && (
        <div className="surface-card">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-slate-100 px-5 py-3">
            <h3 className="text-sm font-semibold text-brand-navy-800">
              Scheduled emails
              <span className="ml-1 font-normal text-slate-500">
                · {schedules.length} total · {active} active{failingCount ? ` · ` : ''}
                {failingCount > 0 && <span className="text-rose-600">{failingCount} failing</span>}
              </span>
            </h3>
            <p className="text-xs text-slate-400">Every send re-prices the borrower's saved scenario on the current rates · click a name to see it</p>
          </div>
          <ul className="divide-y divide-slate-100">
            {schedules.map((s) => (
              <Fragment key={s.id}>
                <li className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3">
                  <BorrowerCell name={s.borrowerName} email={s.toEmail} onOpen={() => onOpenScenario(s)} />
                  <WhatCell
                    title={s.format === 'grid' ? 'Best-rates email' : `Options email · ${s.productIds.length || 'all'} option${s.productIds.length === 1 ? '' : 's'}`}
                    detail={[
                      s.format === 'grid' ? programsLabel(s.programKeys) : null,
                      s.cypBps ? `CYP ${s.cypBps}` : null,
                      lendersLabel(s.lenders),
                      s.transport === 'sendgrid' ? 'via SendGrid' : null,
                      s.scenarioSavedAt ? `scenario saved ${fmtWhen(s.scenarioSavedAt)}` : 'sends its own copy of the scenario — open it and Save to keep it',
                      s.legacySource ? `moved from ${s.legacySource === 'pricing' ? 'Pricing' : 'Other Lenders'}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                  <StatusCell active={s.active} lastStatus={s.lastStatus} nextRunAt={s.nextRunAt} intervalMinutes={s.intervalMinutes} sentCount={s.sentCount} />
                  <div className="ml-auto flex shrink-0 items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => onOpenScenario(s)} title="Show the scenario this email sends in the panel — edit and Save to change it">
                      Scenario
                    </Button>
                    <Button size="sm" variant="ghost" disabled={previewMut.isPending} onClick={() => previewMut.mutate(s)} title="See the next email and the lender behind each rate">
                      {previewMut.isPending && previewMut.variables?.id === s.id ? 'Pricing…' : 'Preview'}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={testMut.isPending} onClick={() => testMut.mutate(s)} title="Send the next email to yourself — the borrower gets nothing">
                      {testMut.isPending && testMut.variables?.id === s.id ? 'Sending…' : 'Test to me'}
                    </Button>
                    <RowMenu
                      label={`More actions for ${s.borrowerName}`}
                      items={[
                        { label: 'Rates sent', onClick: () => onShowSent({ title: `Rates sent to ${s.borrowerName}`, scheduleId: s.id }) },
                        {
                          label: 'Reschedule',
                          onClick: () => {
                            setEditId(editId === s.id ? null : s.id);
                            setEditStart(isoToLocalInput(s.nextRunAt));
                          },
                        },
                        { label: s.active ? 'Pause' : 'Resume', onClick: () => patchMut.mutate({ id: s.id, active: !s.active }) },
                        {
                          label: 'Cancel schedule',
                          danger: true,
                          onClick: () => {
                            if (window.confirm(`Cancel ${s.borrowerName}'s recurring email?`)) delMut.mutate(s.id);
                          },
                        },
                      ]}
                    />
                  </div>
                </li>
                {editId === s.id && (
                  <li className="bg-slate-50/70 px-5 py-2.5">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-slate-600">Next send for {s.borrowerName}</span>
                      <input type="datetime-local" value={editStart} onChange={(e) => setEditStart(e.target.value)} className="rounded border border-slate-300 px-1.5 py-0.5 text-slate-800" />
                      <Button
                        size="sm"
                        onClick={() => {
                          const iso = localInputToIso(editStart);
                          if (iso) patchMut.mutate({ id: s.id, startAt: iso });
                        }}
                        disabled={!editStart || patchMut.isPending}
                      >
                        {patchMut.isPending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>
                        Cancel
                      </Button>
                      <span className="text-slate-400">Must be a future date and time.</span>
                    </div>
                  </li>
                )}
              </Fragment>
            ))}
          </ul>
        </div>
      )}

      {preview && (
        <EmailPreviewDialog
          content={preview.content}
          onClose={() => setPreview(null)}
          actions={
            preview.legacy ? (
              <>
                <Button size="sm" variant="secondary" disabled={legacyTestMut.isPending} onClick={() => legacyTestMut.mutate(preview.legacy!)}>
                  {legacyTestMut.isPending ? 'Sending…' : 'Send test to me'}
                </Button>
                <Button size="sm" variant="gold" disabled={moveMut.isPending || !preview.content.html} onClick={() => confirmMove(preview.legacy!)}>
                  {moveMut.isPending ? 'Moving…' : 'Move to Mana Pricing'}
                </Button>
              </>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

const KIND_LABEL: Record<ManaSendLogDto['kind'], string> = { email: 'Sent now', schedule: 'Scheduled', test: 'Test to you', preview: 'Preview' };

/** The last emails for a schedule or borrower, with the lender, product and price behind every rate. */
export function SentLogDialog({ target, onClose }: { target: SentLogTarget; onClose: () => void }) {
  const q = new URLSearchParams({ limit: '20', ...(target.scheduleId ? { scheduleId: target.scheduleId } : {}), ...(target.borrowerName ? { borrowerName: target.borrowerName } : {}) });
  const { data, isLoading, error } = useQuery({ queryKey: ['mana-send-logs', q.toString()], queryFn: () => api<{ logs: ManaSendLogDto[] }>(`/api/mana-pricing/send-logs?${q.toString()}`) });
  const [openId, setOpenId] = useState<string | null>(null);
  const logs = data?.logs ?? [];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={target.title} className="surface-card my-6 w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-brand-navy-800">{target.title}</h3>
            <p className="mt-0.5 text-xs text-slate-500">Only you see the lenders — the borrower's email never names one.</p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="px-5 py-3">
          {isLoading ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : error ? (
            <p className="text-xs text-rose-600">{errText(error, 'Could not load the log')}</p>
          ) : logs.length === 0 ? (
            <p className="text-xs text-slate-500">No Mana Pricing emails yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {logs.map((l) => {
                const isOpen = openId === l.id;
                return (
                  <li key={l.id} className="py-2">
                    <button type="button" onClick={() => setOpenId(isOpen ? null : l.id)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left text-xs">
                      <span className="w-3 text-slate-400">{isOpen ? '▾' : '▸'}</span>
                      <span className="font-medium text-slate-800">{fmtWhen(l.createdAt)}</span>
                      <Badge tone={l.status === 'sent' ? 'success' : 'danger'}>{l.status === 'sent' ? KIND_LABEL[l.kind] : `Failed · ${KIND_LABEL[l.kind]}`}</Badge>
                      <span className="text-slate-500">{l.format === 'grid' ? 'Best-rates email' : 'Options email'}</span>
                      <span className="text-slate-500">to {l.toEmail}</span>
                      {l.error && <span className="text-rose-600">{l.error}</span>}
                    </button>
                    {isOpen && (
                      <div className="mt-2 space-y-2 pl-6">
                        {l.notes.length > 0 && (
                          <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-amber-800">
                            {l.notes.map((n) => (
                              <li key={n}>{n}</li>
                            ))}
                          </ul>
                        )}
                        {l.cells.length > 0 && (isRealtorCells(l.cells as ManaGridCell[]) ? <RealtorCellsTable cells={l.cells as ManaGridCell[]} /> : <GridCellsTable cells={l.cells as ManaGridCell[]} />)}
                        {l.rows.length > 0 && <OptionsRowsTable rows={l.rows as ManaOptionsSentRow[]} />}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
